import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Box,
    Typography,
    Alert,
    Skeleton
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import i18n from '../../i18n';
import { useEffect, useState } from 'react';

export const ShowKeysDialog = ({
    terminalId,
    openDialog,
    closeDialog,
    server,
    allowedAction
}) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [isFirstRender, setIsFirstRender] = useState(true);
    const fetchData = async (id) => {
        const response = await server.getTerminalKeys(id);
        if (response.error === 'OK') {
            setData(response.data[0]);
        } else setError(response.error);
    };
    const resetKeys = async () => {
        const terminalIdNumber = parseInt(terminalId);
        const response = await server.resetApiKeys(terminalId);
        if (response.error === 'OK') {
            fetchData(terminalIdNumber);
        } else setError(response.error);
    };
    const closeAndClearData = () => {
        setData(null);
        setIsFirstRender(true);
        closeDialog();
    };
    const copyAllKeys = () => {
        navigator.clipboard.writeText(
            `API Key: ${data.apiKey}\nSign Key: ${data.signKey}`
        );
    };
    useEffect(() => {
        if (openDialog && terminalId && isFirstRender) {
            let terminalIdNumber = parseInt(terminalId);
            fetchData(terminalIdNumber);
            setIsFirstRender(false);
        }
    }, [terminalId, openDialog]);
    const isAllowedReset = allowedAction('reset-api-keys');
    return (
        <Dialog
            open={Boolean(openDialog)}
            onClose={closeAndClearData}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>{i18n.t('common.showKeys')}</DialogTitle>
            <DialogContent>
                {error && (
                    <Stack spacing={2}>
                        <Alert severity="error">{error}</Alert>
                    </Stack>
                )}
                {data ? (
                    <Stack spacing={2}>
                        <Typography variant="body1">
                            {i18n.t('common.signKey')}:
                        </Typography>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            p={1}
                            border={1}
                            borderRadius={1}
                            sx={{
                                minWidth: '300px',
                                borderColor: 'rgba(241,236,236,0.54)'
                            }}
                        >
                            <Box>{data.signKey || '-'}</Box>
                            <IconButton
                                onClick={() =>
                                    navigator.clipboard.writeText(
                                        data.signKey || ''
                                    )
                                }
                            >
                                <ContentCopyIcon />
                            </IconButton>
                        </Box>
                        <Typography variant="body1">
                            {i18n.t('common.apiKey')}:
                        </Typography>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            p={1}
                            border={1}
                            borderRadius={1}
                            sx={{
                                minWidth: '300px',
                                borderColor: 'rgba(241,236,236,0.54)'
                            }}
                        >
                            <Box>{data.apiKey || '-'}</Box>
                            <IconButton
                                onClick={() =>
                                    navigator.clipboard.writeText(
                                        data.apiKey || ''
                                    )
                                }
                            >
                                <ContentCopyIcon />
                            </IconButton>
                        </Box>
                        {data.signKey && data.apiKey && (
                            <Button
                                onClick={() => copyAllKeys()}
                                color="secondary"
                                variant="outlined"
                            >
                                {i18n.t('common.copyAllKeys')}
                            </Button>
                        )}
                        {isAllowedReset && (
                            <Button
                                onClick={() => resetKeys()}
                                variant="contained"
                            >
                                {i18n.t('common.resetKeys')}
                            </Button>
                        )}
                    </Stack>
                ) : (
                    <ShowKeysDialogSkeleton />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => closeAndClearData()} color="primary">
                    {i18n.t('common.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const ShowKeysDialogSkeleton = () => {
    return (
        <Stack spacing={2}>
            <Typography variant="body1">{i18n.t('common.signKey')}:</Typography>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                p={1}
                border={1}
                borderRadius={1}
                sx={{ minWidth: '300px', borderColor: 'rgba(241,236,236,0.54)' }}
            >
                <Skeleton variant="text" width="80%" height={24} />
                <Skeleton variant="circular" width={24} height={24} />
            </Box>
            <Typography variant="body1">{i18n.t('common.apiKey')}:</Typography>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                p={1}
                border={1}
                borderRadius={1}
                sx={{ minWidth: '300px', borderColor: 'rgba(241,236,236,0.54)' }}
            >
                <Skeleton variant="text" width="80%" height={24} />
                <Skeleton variant="circular" width={24} height={24} />
            </Box>
            <Skeleton variant="rectangular" height={36} width="100%" />
            <Skeleton variant="rectangular" height={36} width="100%" />
        </Stack>
    );
};
